import React from 'react';
import { motion } from 'framer-motion';
import {
  Mail, MessageCircle, MapPin, Clock
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const ContactPage = () => {

  useSEO({
    title: 'Contact Us | AGO Innovations — Assignment Help & Finance Services',
    description:
      'Get in touch with AGO Innovations for assignment writing help, insurance advisory, investment planning, or student earning opportunities. WhatsApp, email, or contact form — we respond within 24 hours.',
    keywords:
      'contact AGO Innovations, assignment help contact, insurance advisor contact, student services contact, AGO Innovations support',
    canonical: 'https://agoinnovations.in/contact',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact AGO Innovations',
      url: 'https://agoinnovations.in/contact',
      mainEntity: {
        '@type': 'Organization',
        name: 'AGO Innovations',
        email: 'support@agoinnovations.in',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Mumbai',
          addressRegion: 'Maharashtra',
          addressCountry: 'IN',
        },
      },
    },
  });

  const contactInfo = [
    { icon: <Mail className="w-5 h-5" />, label: 'Email', value: 'help.agoinnovations@gmail.com', href: 'mailto:help.agoinnovations@gmail.com', color: 'bg-blue-50 text-blue-600' },
    { icon: <MessageCircle className="w-5 h-5" />, label: 'WhatsApp', value: '+91 9322578756', href: 'https://wa.me/919322578756', color: 'bg-green-50 text-green-600' },
    { icon: <MapPin className="w-5 h-5" />, label: 'Location', value: 'Mumbai, Maharashtra, India', href: null, color: 'bg-orange-50 text-orange-600' },
    { icon: <Clock className="w-5 h-5" />, label: 'Response Time', value: 'Within 24 hours', href: null, color: 'bg-purple-50 text-purple-600' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-2xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
            Contact <span className="text-yellow-400">AGO Innovations</span>
          </h1>
          <p className="text-blue-100 text-lg">
            Have a question about our services? We're here to help — reach out via form, WhatsApp, or email.
          </p>
        </motion.div>
      </section>

      {/* CONTACT INFO */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Info */}
          <div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-8">
              <h2 className="text-2xl font-black text-gray-900 mb-2">Get in Touch</h2>
              <p className="text-gray-500 leading-relaxed">
                Whether you need assignment help, insurance guidance, or want to join our Learn & Earn program — we'd love to hear from you.
              </p>
            </motion.div>
            <div className="space-y-4">
              {contactInfo.map((item, i) => (
                <motion.div
                  key={i}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100"
                >
                  <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`}>
                    {item.icon}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                        className="text-gray-900 font-semibold text-sm hover:text-blue-600 transition-colors break-all">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-gray-900 font-semibold text-sm">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
